import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, ClockIcon, FrownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldAlertIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-18" data-mp="1nmjd2trov4uh">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="headline mx-auto max-w-3xl text-center text-2xl text-slate-900 md:text-4xl" data-id="element-19" data-mp="341kvuvdskla0">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border-2 border-celeste/40 bg-white p-6 shadow-sm" data-id="element-20" data-mp="16qc8128be3kq">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-accent/15" data-id="element-21" data-mp="12nxkw5xm4hnn" />
      <div className="mb-4 text-sm text-gold" data-id="element-22" data-mp="xqfd4ivc08h8">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-23" data-mp="xs05q2y35hcg">
        “{item.text}”
      </p>
      <div className="flex items-center gap-3 border-t border-celeste/40 pt-4" data-id="element-24" data-mp="o0clon3uz2l0">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-celeste bg-sky-50 font-display text-sm text-primary" data-id="element-25" data-mp="ny9mbcgtfx3m">
          {item.initials}
        </span>
        <div data-id="element-26" data-mp="exl8ahrf9wqq">
          <p className="text-sm font-bold text-slate-900" data-id="element-27" data-mp="a01ufiymzlzb">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-28" data-mp="a2e7bhpjf65i">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-14 md:py-16" data-id="element-29" data-mp="3riqueeuvdhj9">
      <Eyebrow data-id="element-30" data-mp="cpjq7etgpy5">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-31" data-mp="eab5o5u3srs">
        Acompañantes Terapéuticos reales que ya aplican{' '}
        <span className="text-accent" data-id="element-32" data-mp="3hlvxg0e8vp73">el sistema</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-33" data-mp="3mje0ja5kza76">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-34" data-mp="e3kj8t3fdup2" />)}
      </div>
      <div className="mx-auto mt-8 max-w-3xl rounded-3xl border-4 border-celeste bg-white p-6 text-center shadow-xl shadow-celeste/20" data-id="element-35" data-mp="j3767u922pb2">
        <p className="text-sm font-extrabold uppercase tracking-wide text-accent" data-id="element-36" data-mp="4d9fp7nnlnip">
          ⏰ Oferta de lanzamiento por tiempo limitado
        </p>
        <p className="headline mt-2 text-xl text-slate-900" data-id="element-37" data-mp="9b14vtwwyvj7">
          Sumate hoy y dejá tu próxima sesión{' '}
          <span className="text-accent" data-id="element-38" data-mp="11tom2y8shqf0">planificada en 5 minutos.</span>
        </p>
        <div className="mt-5" data-id="element-39" data-mp="11vj8kgzplql7">
          <Button href="#pricing" className="w-full text-xs md:w-auto md:text-base" data-id="element-40" data-mp="2mjk7rislfe1l">
            QUIERO VER MI OFERTA DEL COMBO COMPLETO AT
          </Button>
        </div>
      </div>
    </section>;
}
const pains = ['Repetís siempre las mismas actividades porque no tenés variedad clasificada por diagnóstico.', 'No tenés un protocolo claro ante una crisis, ideación de riesgo o desregulación severa.', 'No tenés documentación que te respalde ante la obra social ni ante el equipo tratante.', 'Tardás horas armando registros e informes que podrían estar listos en cinco minutos.', 'Cada sesión queda desconectada de la anterior, sin progresión real que puedas mostrar.'];
const transformations = ['Sin buscar actividades a último momento', 'Con intervención elegida por diagnóstico', 'Registro completo en cinco minutos', 'Respaldo profesional en cada sesión'];
export function PainAndTransformation() {
  return <>
      <section className="border-y-2 border-celeste/40 bg-white px-4 py-12 md:py-16" data-id="element-41" data-mp="3jz7xd4osjnro">
        <div className="mx-auto max-w-4xl" data-id="element-42" data-mp="j5upmu5ujgsj">
          <Eyebrow danger data-id="element-43" data-mp="n5vc9233myao">😤 La realidad</Eyebrow>
          <SectionTitle data-id="element-44" data-mp="n6dkyy6y74zh">
            ¿Cuántas veces más vas a improvisar con un{' '}
            <span className="text-accent" data-id="element-45" data-mp="s3ww3p8cn1n7">paciente enfrente?</span>
          </SectionTitle>
          <p className="mx-auto mt-6 max-w-3xl text-center font-semibold text-slate-600" data-id="element-46" data-mp="x4b5baloshmt">
            Amás lo que hacés. Pero nadie te preparó para esto:
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-47" data-mp="164ps6eeau4sf">
            {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-celeste/40 bg-sky-50/60 p-4" data-id="element-48" data-mp="1avpsltmmn4zo">
                <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-49" data-mp="1fwn6vv4inevw" />
                <span className="text-sm font-medium text-slate-700 md:text-base" data-id="element-50" data-mp="1kv6x47q57cu2">{pain}</span>
              </div>)}
          </div>
          <div className="mt-10 rounded-2xl border-2 border-celeste bg-sky-50 p-6 text-center" data-id="element-51" data-mp="3aar8toubpunt">
            <p className="headline text-xl text-slate-800 md:text-2xl" data-id="element-52" data-mp="35bo64b1tg0j0">
              No es falta de ganas.
              <br data-id="element-53" data-mp="30cbhbkrdwjds" />
              <span className="text-accent md:text-3xl" data-id="element-54" data-mp="30bs3af4uuf51">Es falta de sistema.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-55" data-mp="2vfl4r7esg5x4">
        <Eyebrow data-id="element-56" data-mp="3t07gqx8c8pe9">
          <span className="inline-flex items-center gap-2" data-id="element-57" data-mp="3t0pfq1mdxhpv">
            <SparklesIcon className="h-4 w-4" data-id="element-58" data-mp="3o1w3sdmqxyrt" /> Con el Combo Completo AT
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-59" data-mp="3jantlfdle9dl">
          ¿Y si la próxima sesión fuera{' '}
          <span className="text-accent" data-id="element-60" data-mp="3eccjr4naryvd">completamente diferente?</span>
        </SectionTitle>
        <div className="mt-8 rounded-3xl border-2 border-celeste bg-sky-50/70 p-6 shadow-[0_10px_40px_rgba(14,165,233,.12)] md:p-10" data-id="element-61" data-mp="fmce1olaayhi">
          <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-xl" data-id="element-62" data-mp="avdtgct9je8g">
            Abrís el tomo del diagnóstico que corresponde. Elegís la intervención. Completás el
            registro en cinco minutos. Y listo.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-63" data-mp="2a4drpivcb3ja">
            {transformations.map(item => <div key={item} className="rounded-xl border border-celeste bg-white p-3 text-center text-xs font-bold text-primary-dark md:text-sm" data-id="element-64" data-mp="20chuea12vhg4">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-semibold text-slate-600" data-id="element-65" data-mp="1qch7yf7wwl7p">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-66" data-mp="1cgx14i5gjtgn">
              Combo Completo Acompañamiento Terapéutico 2026.
            </strong>
          </p>
        </div>
        <div className="mt-8 text-center" data-id="element-67" data-mp="36tv6qog7q1xn">
          <Button href="#pricing" className="w-full text-xs md:w-auto md:text-base" data-id="element-68" data-mp="36qqy9evpsk16">
            QUIERO EL COMBO COMPLETO AT
          </Button>
        </div>
      </section>
    </>;
}
const values = ['Más de 250 protocolos técnicos', '6 Tomos completos', '50 casos clínicos resueltos', 'Sesiones listas para aplicar', 'Creado para Acompañantes Terapéuticos', 'Adaptado a Argentina 2026'];
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-69" data-mp="151kzkee2wzw6">
      <Eyebrow data-id="element-70" data-mp="1iu19lz5edy6l">
        <span className="inline-flex items-center gap-2" data-id="element-71" data-mp="1ithpqcw6szpx">
          <TrophyIcon className="h-4 w-4" data-id="element-72" data-mp="1dwhja0vfduya" /> El sistema AT 2026
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-73" data-mp="1ssyrwkc8cvh5">
        6 tomos. <span className="text-accent" data-id="element-74" data-mp="1ss74hwsse41y">Todos los contextos cubiertos.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-semibold text-slate-600" data-id="element-75" data-mp="1nv73i0asnrhp">
        De la evaluación inicial al informe de cierre. Todo listo para aplicar hoy.
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-76" data-mp="3plik3j6qjge3">
        {values.map(value => <div key={value} className="flex items-center gap-3 rounded-xl border-2 border-celeste/40 bg-white p-4 shadow-sm" data-id="element-77" data-mp="33smj2deq6xbd">
            <span className="rounded-lg bg-celeste/20 p-2" data-id="element-78" data-mp="38plfj2lv3gcq">
              <CheckIcon className="h-4 w-4 text-accent" data-id="element-79" data-mp="3dhwfu6kil45t" />
            </span>
            <span className="text-sm font-bold leading-tight text-slate-800" data-id="element-80" data-mp="3igzijkd0uyam">{value}</span>
          </div>)}
      </div>

      <div className="mt-16" data-id="element-81" data-mp="2kuimsjjxssqz">
        <h3 className="headline text-center text-xl text-slate-900 md:text-3xl" data-id="element-82" data-mp="2kvbkyw4ty9xi">
          Los 6 tomos que necesitás para{' '}
          <span className="text-accent" data-id="element-83" data-mp="2urof1v4aepss">intervenir con criterio en cualquier caso.</span>
        </h3>
        <p className="mt-3 text-center text-sm font-semibold text-slate-500" data-id="element-84" data-mp="2yt9vtrjxzo89">
          Tocá un tomo para ver el contenido
        </p>
        <div className="mt-8 space-y-3" data-id="element-85" data-mp="a0e77wwx3t4c">
          {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border-2 bg-white transition-colors ${open === index ? 'border-accent shadow-lg shadow-celeste/20' : 'border-celeste/40'}`} data-id="element-86" data-mp="e2hs1c5h8a2a" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-87" data-mp="1uk9vg92cski5">
                <span className="flex items-center gap-3" data-id="element-88" data-mp="1fuu52u6qih2z">
                  <span className="text-2xl" data-id="element-89" data-mp="1fs7qwbpc93bs">{tomo.icon}</span>
                  <span data-id="element-90" data-mp="1atxsls8bhmly">
                    <b className="block text-slate-900" data-id="element-91" data-mp="1axlk6lu7byy1">
                      Tomo {index + 1} — {tomo.name}
                    </b>
                    <small className="font-bold text-accent" data-id="element-92" data-mp="11vcbm1ihufna">{tomo.period}</small>
                  </span>
                </span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-93" data-mp="316lw4wyehotd" />
              </button>
              <AnimatePresence initial={false} data-id="element-94" data-mp="2543frlc1tjlm">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} data-id="element-95" data-mp="206usx7fygre1">
                    <ul className="space-y-2 border-t border-celeste/40 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-96" data-mp="2981aljucc88v">
                      {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-97" data-mp="1hglhr826xpa7">
                          <span className="text-accent" data-id="element-98" data-mp="1v6yppaf313zj">→</span>
                          {line}
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="mt-10 rounded-3xl border-4 border-gold bg-primary p-6 text-center shadow-[0_20px_50px_rgba(2,132,199,.35)] md:p-8" data-id="element-99" data-mp="xrktzn71vlkf">
          <p className="font-display text-base text-amber-300 md:text-xl" data-id="element-100" data-mp="39dvdok6sov4n">
            🔥 Ya sabés todo lo que hay adentro
          </p>
          <p className="mt-2 text-sm font-bold text-white md:text-lg" data-id="element-101" data-mp="3e7r85cnunzv2">
            Tu próxima sesión puede estar planificada hoy mismo, con protocolo, actividades e informe
            listos para usar.
          </p>
          <div className="mt-6" data-id="element-102" data-mp="35aytip1lwqyl">
            <Button href="#pricing" fullWidth className="border-b-4 border-amber-600 bg-gold text-xs hover:bg-amber-600 md:text-base" data-id="element-103" data-mp="3t2npqghjtzkt">
              💥 DAME ACCESO AHORA A LOS 6 TOMOS Y LOS 6 BONOS →
            </Button>
          </div>
          <p className="mt-3 text-[11px] font-semibold text-sky-100" data-id="element-104" data-mp="3o3b05nzameej">
            Acceso en 5 minutos · Garantía de 7 días · Pago seguro con Mercado Pago
          </p>
        </div>
      </div>
    </section>;
}
const beforeAfterRows = [['Improvisás cada sesión', 'Abrís el tomo y ejecutás con criterio'], ['Sin protocolo ante una crisis', 'Protocolo exacto para cada emergencia'], ['Horas armando informes', 'Registro profesional listo en cinco minutos'], ['Sin respaldo ante la obra social', 'Documentación profesional en cada sesión'], ['Sesiones sin progresión', 'Progresión trimestral clara por paciente']];
export function BeforeAfterAndBonuses() {
  return <>
      <section className="border-y-2 border-celeste/40 bg-white px-4 py-12 md:py-16" data-id="element-105" data-mp="19vtf259fmcmf">
        <div className="mx-auto max-w-5xl" data-id="element-106" data-mp="14wyjs3ua2vlf">
          <Eyebrow data-id="element-107" data-mp="14vxbu88k6p7x">🔄 Sin sistema vs. con el Combo Completo</Eyebrow>
          <SectionTitle data-id="element-108" data-mp="10v3nxev49af1">
            No comprás archivos. <span className="text-accent" data-id="element-109" data-mp="10vwsc96lrnu0">Comprás un sistema.</span>
          </SectionTitle>
          <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-110" data-mp="vxv0ayr78e6e">
            <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-6" data-id="element-111" data-mp="2v9o1l7l0s5ox">
              <h3 className="mb-5 flex items-center gap-2 font-extrabold text-slate-900" data-id="element-112" data-mp="2qakwxo8yc1hh">
                <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={40} data-id="element-113" data-mp="2xtu7hkt72684" />
                Sin sistema
              </h3>
              {beforeAfterRows.map(([before]) => <p key={before} className="mb-3 flex gap-2 text-sm font-medium text-slate-600" data-id="element-114" data-mp="32qkku0dm6npu">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-115" data-mp="37lzvkjgbs3i1" />
                  {before}
                </p>)}
            </div>
            <div className="rounded-2xl border-4 border-accent bg-white p-6 shadow-lg shadow-celeste/20" data-id="element-116" data-mp="2evzw504lfimk">
              <h3 className="mb-5 flex items-center gap-2 font-extrabold text-slate-900" data-id="element-117" data-mp="2juugi6i7lf8s">
                <CheckIcon className="rounded-lg bg-celeste/20 p-2 text-accent" size={40} data-id="element-118" data-mp="2os32yjae45fu" />
                Con el Combo Completo AT
              </h3>
              {beforeAfterRows.map(([, after]) => <p key={after} className="mb-3 flex gap-2 text-sm font-bold text-slate-800" data-id="element-119" data-mp="425a2b1tv8ad">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-120" data-mp="83gy4c51zlgg" />
                  {after}
                </p>)}
            </div>
          </div>
          <p className="headline mt-10 text-center text-lg text-slate-800 md:text-2xl" data-id="element-121" data-mp="1orxtda6obwc">
            Recuperás <span className="text-accent" data-id="element-122" data-mp="gm284jb3bbqh">tiempo, criterio</span> y tu lugar en el equipo
            interdisciplinario.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:py-16" data-id="element-123" data-mp="boiv1mq4p504">
        <Eyebrow data-id="element-124" data-mp="pkwakqb3qwby">
          <span className="inline-flex items-center gap-2" data-id="element-125" data-mp="pke8hd1rdnw4">
            <GiftIcon className="h-4 w-4" data-id="element-126" data-mp="knw4wgi5zvld" /> Bonos exclusivos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-127" data-mp="zca66mzxnbzt">
          Además te llevás <span className="text-accent" data-id="element-128" data-mp="zb8tke39qhfz">6 bonos incluidos gratis</span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-129" data-mp="udfoxphm5vht">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border-2 border-celeste/40 bg-white p-5 shadow-sm" data-id="element-130" data-mp="14bddke3dxuay">
              <div className="flex gap-3" data-id="element-131" data-mp="1mxb3batgxn85">
                <span className="text-3xl" data-id="element-132" data-mp="1rz0fitsfg6xt">{icon}</span>
                <div data-id="element-133" data-mp="1rwe8cv9g7tg4">
                  <small className="font-extrabold uppercase tracking-wide text-accent" data-id="element-134" data-mp="1dypnvojamobl">
                    Bono {number}
                  </small>
                  <h3 className="font-extrabold text-slate-900" data-id="element-135" data-mp="1iwhd2asjzwc3">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-136" data-mp="147l8inpph1g8">
                {description}
              </p>
              <div className="flex border-t border-celeste/40 pt-4 text-sm font-bold" data-id="element-137" data-mp="v8hqzo66su3u">
                <span className="text-slate-400 line-through" data-id="element-138" data-mp="z9t815azw70f">Valor: {value}</span>
                <span className="ml-auto text-accent" data-id="element-139" data-mp="z7qfoemde1sj">GRATIS</span>
              </div>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border-2 border-celeste bg-sky-50 p-6 text-center" data-id="element-140" data-mp="q9oadmvlsmzg">
          <p className="font-semibold text-slate-600" data-id="element-141" data-mp="2rykdmk9fd7rd">
            Valor total de bonos: <s className="font-bold text-slate-400" data-id="element-142" data-mp="2n7u8n84wv6x7">$72.000 ARS</s>
          </p>
          <p className="headline mt-1 text-xl text-slate-900" data-id="element-143" data-mp="2n5r83u64tve8">
            Hoy van <span className="text-accent" data-id="element-144" data-mp="2j6ik75xrwrql">incluidos gratis</span> con tu compra.
          </p>
          <div className="mt-6" data-id="element-145" data-mp="2e8gy0c51wcaw">
            <Button href="#pricing" fullWidth className="text-xs md:text-sm" data-id="element-146" data-mp="2e52vn3we033g">
              SÍ, QUIERO MIS 6 BONOS GRATIS ANTES DE QUE SUBA EL PRECIO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
export function SocialAndFAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-147" data-mp="2wxnlecgshs31">
      <Eyebrow data-id="element-148" data-mp="2wx5iiwzmgfm5">
        <span className="inline-flex items-center gap-2" data-id="element-149" data-mp="2wzrlejbki6y1">
          <HelpCircleIcon className="h-4 w-4" data-id="element-150" data-mp="1qi4gc9k07174" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-151" data-mp="1lhgt7yywb4i6">
        Lo que más <span className="text-accent" data-id="element-152" data-mp="1lkbefspqgtcn">nos preguntan</span>
      </SectionTitle>
      <div className="mt-10 space-y-3" data-id="element-153" data-mp="1bfuzd1k2vg9w">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border-2 bg-white ${open === index ? 'border-accent shadow-lg shadow-celeste/20' : 'border-celeste/40'}`} data-id="element-154" data-mp="1gdwghgxy15iq" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-155" data-mp="1q4is702jhbw5">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-156" data-mp="243ii0b1sfwtw" />
            </button>
            <AnimatePresence initial={false} data-id="element-157" data-mp="9q9ceucd4vgx">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-celeste/40 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-158" data-mp="en9bglasoxyj">
                  {answer}
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
const costs = [[ClockIcon, '150 horas de planificación improvisada por año', 'Tiempo que podrías recuperar desde hoy con los 6 tomos, las actividades clasificadas y los formularios ya listos.'], [AlertCircleIcon, 'Riesgo profesional ante cada crisis sin protocolo', 'Sin un procedimiento escrito para crisis, ideación de riesgo o desregulación severa, cada episodio se resuelve por intuición y expone tu responsabilidad profesional.'], [FrownIcon, 'Informes que la obra social cuestiona', 'Sin registros con lenguaje técnico y evolución documentada, las rendiciones se demoran, se observan o se rechazan.'], [ShieldAlertIcon, 'Un lugar en el equipo que nunca se consolida', 'Sin objetivos medibles ni progresión demostrable, el aporte del AT queda invisible en las reuniones interdisciplinarias.']] as const;
export function GuaranteeAndCost() {
  return <>
      <section className="border-y-2 border-celeste/40 bg-sky-50/70 px-4 py-12 md:py-16" data-id="element-159" data-mp="3gln715zhg5v5">
        <div className="mx-auto max-w-3xl text-center" data-id="element-160" data-mp="863fcbegmvi7">
          <span className="inline-flex rounded-full border-4 border-accent bg-celeste/20 p-5 shadow-lg shadow-celeste/25" data-id="element-161" data-mp="d57ox35aqv92">
            <ShieldIcon className="h-10 w-10 text-accent" data-id="element-162" data-mp="3ukehmif4z9ej" />
          </span>
          <Eyebrow data-id="element-163" data-mp="3ed5s7nckbru">🛡️ Sin riesgo · 100% garantía</Eyebrow>
          <SectionTitle data-id="element-164" data-mp="3csaugx4eqtg">
            Probalo 7 días. Si no te sirve,{' '}
            <span className="text-accent" data-id="element-165" data-mp="r2wdakykokiv">te devolvemos el 100%.</span>
          </SectionTitle>
          <div className="mt-7 rounded-2xl border-2 border-celeste/40 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-166" data-mp="vwqrj8ves7ak">
            <p data-id="element-167" data-mp="2sfd7m792c20l">
              Usás el sistema en tus sesiones.{' '}
              <b className="text-slate-900" data-id="element-168" data-mp="2sek37cxktolm">
                Lo aplicás con tus pacientes, con la familia y con el equipo tratante.
              </b>
            </p>
            <p className="mt-3" data-id="element-169" data-mp="2ioicsehr3u3j">
              Y si en 7 días sentís que no era lo que esperabas, escribís a
              compradigitalyaa@gmail.com y te devolvemos el dinero completo.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-170" data-mp="28sf7x0vilhcw">
              {['Sin formularios', 'Sin vueltas', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-171" data-mp="1zq5zqhnpy02o">
                  <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-172" data-mp="1zsiqnkbgfkrv" />
                  {text}
                </span>)}
            </div>
          </div>
          <p className="headline mt-6 text-xl text-slate-900" data-id="element-173" data-mp="3u46yz3n6kp3a">
            El riesgo no es tuyo. <span className="text-accent" data-id="element-174" data-mp="1ii548wwu2bam">Es nuestro.</span>
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-id="element-175" data-mp="1dish0b21v27i">
            <Metric value="<1%" label="Tasa de devoluciones reales" data-id="element-176" data-mp="1dgfsff1sdtlh" />
            <Metric value="+500" label="Acompañantes con el sistema" data-id="element-177" data-mp="1s8pxs5xngpmo" />
            <Metric value="7 días" label="Garantía sin condiciones" data-id="element-178" data-mp="1ng6jt7v1vshc" />
          </div>
        </div>
      </section>

      <section className="border-y-2 border-celeste/40 bg-white px-4 py-12 md:py-16" data-id="element-179" data-mp="216jtbenlbf8s">
        <div className="mx-auto max-w-4xl" data-id="element-180" data-mp="1w7qf1k0hbk7k">
          <Eyebrow danger data-id="element-181" data-mp="1wauq91cd3g7q">⚖️ El costo real</Eyebrow>
          <SectionTitle data-id="element-182" data-mp="2b6an1e4btq7t">
            ¿Cuánto te cuesta <span className="text-red-500" data-id="element-183" data-mp="2b3y8zc1ow001">NO tenerlo?</span>
          </SectionTitle>
          <div className="mt-10 grid gap-3 sm:grid-cols-2" data-id="element-184" data-mp="267podj4q3elh">
            {costs.map(([CostIcon, label, cost]) => <div key={label} className="flex gap-3 rounded-xl border border-celeste/40 bg-sky-50/50 p-4 shadow-sm" data-id="element-185" data-mp="gu457wgxgb6j">
                <span className="h-fit rounded-lg border border-red-100 bg-red-50 p-2" data-id="element-186" data-mp="11idnddkviydq">
                  <CostIcon className="h-5 w-5 text-red-500" data-id="element-187" data-mp="15jp40tlrs99s" />
                </span>
                <p data-id="element-188" data-mp="1ahh8so7ijpve">
                  <b className="block text-sm text-slate-900" data-id="element-189" data-mp="1agz4d4ap65ce">{label}</b>
                  <span className="text-sm font-medium text-slate-500" data-id="element-190" data-mp="1fhd3fwubm4ya">{cost}</span>
                </p>
              </div>)}
          </div>
          <p className="headline mt-10 rounded-2xl border-2 border-celeste bg-white p-6 text-center text-lg text-slate-800 shadow-sm md:text-2xl" data-id="element-191" data-mp="1kfoe29sfwjhk">
            La próxima sesión puede ser igual.
            <br data-id="element-192" data-mp="1t9092na5kptq" />
            <span className="text-accent" data-id="element-193" data-mp="1takv1oiej0fz">O puede ser la primera con un sistema real.</span>
          </p>
          <div className="mt-8 text-center" data-id="element-194" data-mp="236eezsyg9w7s">
            <Button href="#pricing" className="w-full text-xs md:w-auto md:text-base" data-id="element-195" data-mp="237h4bwg4jqll">
              EMPEZAR HOY CON EL SISTEMA COMPLETO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
function Metric({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="rounded-xl border-2 border-celeste/40 bg-white p-3" data-id="element-196" data-mp="3nnf6pf7llcbt">
      <p className="font-display text-xl text-primary" data-id="element-197" data-mp="3iou6hfscisnd">{value}</p>
      <p className="mt-1 text-[10px] font-bold leading-tight text-slate-500" data-id="element-198" data-mp="3irgf7iqz3e76">{label}</p>
    </div>;
}